import { useCallback } from 'react';
import { useQuery, useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { SUPER_USER } from 'shared/constants/permissions';
import { getImpersonatedUser } from 'auth';

const USER_QUERY = gql`
  query ($impersonatedId: ID) {
    user(impersonatedId: $impersonatedId) {
      id
      email
      firstname
      lastname
      phone
      mfa
      defaultMfa
      totpEnabled
      mfaMethods
      roles { id name }
      permissions
      customerAccess {
        id
        customer { id name }
        roles { id name }
        permissions
        dataSourceAccess {
          id
          dataSource { 
            id
            title
            website { id } 
          }
          roles { id name }
          permissions
        }
      }
      impersonator {
        id
        email
      }
      globalSettings(keys: ["selectedCustomer"])
    }
  }
`;

const UPDATE_SETTINGS = gql`
  mutation ($input: [SettingInput!]!) {
    updateCurrentUserSettings(input: $input) {
      id
    }
  }
`;

const useUser = () => {
  const impersonatedId = getImpersonatedUser();
  const { loading, data, refetch } = useQuery(USER_QUERY, {
    variables: {
      impersonatedId,
    },
    fetchPolicy: 'cache-first',
  });

  const [updateSettingsMutation] = useMutation(UPDATE_SETTINGS);

  const user = data?.user;

  const isSuperUser = user?.permissions.includes(SUPER_USER);

  const hasPermission = useCallback((permission) => isSuperUser || user?.permissions.some(p => p.indexOf(permission) === 0), [isSuperUser, user]);

  const updateUserSetting = useCallback(async ({ relationId, setting, value }) => {
    await updateSettingsMutation({
      variables: {
        input: [{
          relationId,
          key: setting,
          value,
        }],
      },
    });
    refetch();
  }, [updateSettingsMutation, refetch]);

  const updateUserCustomerSetting = useCallback(async ({ setting, value }) => {
    await updateSettingsMutation({
      variables: {
        input: [{
          relationId: user?.globalSettings.selectedCustomer,
          key: setting,
          value,
        }],
      },
    });
    refetch();
  }, [updateSettingsMutation, refetch, user]);

  const dataSourceAccesses = user?.customerAccess.flatMap(ca => ca.dataSourceAccess);

  const hasCustomerAccess = useCallback((customerId) => isSuperUser || user?.customerAccess?.some(ca => ca.customer.id === customerId), [isSuperUser, user]);

  const hasCustomerPermission = useCallback((customerId, permission) => {
    if (isSuperUser) {
      return true;
    }

    const customerAccess = user?.customerAccess.find((ca) => ca.customer.id === customerId);

    return customerAccess?.permissions.some(p => p.indexOf(permission) === 0) || false;
  }, [isSuperUser, user]);

  const hasDataSourceAccess = useCallback((dataSourceId) => isSuperUser || user?.customerAccess?.some(ca => ca.dataSourceAccess.some((da) => da.dataSource.id === dataSourceId)), [isSuperUser, user]);

  const hasDataSourcePermission = useCallback((dataSourceId, permission) => {
    if (isSuperUser) {
      return true;
    }

    const dataSourceAccess = dataSourceAccesses?.find((da) => da.dataSource.id === dataSourceId);

    return dataSourceAccess?.permissions.some(p => p.indexOf(permission) === 0) || false;
  }, [isSuperUser, user]);

  const hasWebsiteAccess = useCallback((websiteId) => isSuperUser || user?.customerAccess?.some(ca => ca.dataSourceAccess.some((da) => da.dataSource.website?.id === websiteId)), [isSuperUser, user]);

  const changeCustomer = useCallback((customerId) => {
    if (hasCustomerAccess(customerId)) {
      updateUserSetting({ setting: 'selectedCustomer', value: customerId });
    } else {
      throw new Error('User does not have access to this customer');
    }
  }, [updateUserSetting, hasCustomerAccess]);

  return {
    user,
    loading,
    updateUserSetting,
    updateUserCustomerSetting,
    hasPermission,
    hasCustomerAccess,
    hasCustomerPermission,
    hasDataSourceAccess,
    hasDataSourcePermission,
    hasWebsiteAccess,
    changeCustomer,
    refetch,
    isSuperUser,
  };
};

export default useUser;
